import { useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { toast } from 'sonner';
import Editor from '@monaco-editor/react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from '@/components/ui/dialog';
import {
  Mono,
  FamilyBadge,
  RoleBadge,
  StatusBadge,
  StatusDot,
  UtilizationBar,
} from '@/components/common/display';
import { LoadingRows, ErrorState } from '@/components/common/Feedback';
import { DataTable } from '@/components/common/DataTable';
import {
  usePrefix,
  usePrefixChildren,
  usePrefixUtilization,
  useDeletePrefix,
} from '@/hooks/use-prefixes';
import { useAddresses } from '@/hooks/use-addresses';
import { useAudit } from '@/hooks/use-audit';
import { AuditTimeline } from '@/components/audit/AuditTimeline';
import { NextFreeDialog } from '@/components/prefix/NextFreeDialog';
import { EditPrefixDialog } from '@/components/prefix/EditPrefixDialog';
import { RangeExplorerDialog } from '@/components/prefix/RangeExplorerDialog';
import { formatTimestamp, formatRelative, prettyJson, formatNumber } from '@/lib/format';
import { stripHostMask } from '@/lib/ip-utils';
import type { PrefixChild, AddressListItem } from '@/lib/types';
import type { ColumnDef } from '@tanstack/react-table';

export const Route = createFileRoute('/_auth/prefixes/$id')({
  component: PrefixDetailRoute,
});

// ---- Column definitions ----

const childrenColumns: ColumnDef<PrefixChild, unknown>[] = [
  {
    id: 'cidr',
    header: 'CIDR',
    accessorKey: 'cidr',
    cell: ({ row }) => <Mono className="text-[11px]">{row.original.cidr}</Mono>,
  },
  {
    id: 'family',
    header: 'Familia',
    accessorKey: 'family',
    cell: ({ row }) => <FamilyBadge family={row.original.family} />,
  },
  {
    id: 'role',
    header: 'Rol',
    accessorKey: 'role',
    cell: ({ row }) => <RoleBadge role={row.original.role} />,
  },
  {
    id: 'status',
    header: 'Estado',
    accessorKey: 'status',
    cell: ({ row }) => (
      <span className="flex items-center gap-1.5 text-xs">
        <StatusDot status={row.original.status} />
        {row.original.status}
      </span>
    ),
  },
];

const addressColumns: ColumnDef<AddressListItem, unknown>[] = [
  {
    id: 'address',
    header: 'Dirección',
    accessorKey: 'address',
    cell: ({ row }) => (
      <Mono className="text-[11px]">{stripHostMask(row.original.address)}</Mono>
    ),
  },
  {
    id: 'family',
    header: 'Familia',
    accessorKey: 'family',
    cell: ({ row }) => <FamilyBadge family={row.original.family} />,
  },
  {
    id: 'status',
    header: 'Estado',
    accessorKey: 'status',
    cell: ({ row }) => (
      <span className="flex items-center gap-1.5 text-xs">
        <StatusDot status={row.original.status} />
        {row.original.status}
      </span>
    ),
  },
  {
    id: 'hostname',
    header: 'Hostname',
    accessorKey: 'hostname',
    cell: ({ row }) => (
      <span className="text-xs text-muted-foreground">{row.original.hostname || '—'}</span>
    ),
  },
  {
    id: 'device_ref',
    header: 'Dispositivo',
    accessorKey: 'device_ref',
    cell: ({ row }) => (
      <span className="text-xs text-muted-foreground">{row.original.device_ref || '—'}</span>
    ),
  },
];

// ---- Main component ----

function PrefixDetailRoute() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [nextFreeOpen, setNextFreeOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [explorerOpen, setExplorerOpen] = useState(false);

  const { data: prefix, isLoading, error, refetch } = usePrefix(id);
  const deletePrefix = useDeletePrefix();

  function confirmDelete() {
    deletePrefix.mutate(id, {
      onSuccess: () => {
        toast.success(`Prefijo ${prefix?.cidr ?? id} eliminado`);
        setDeleteOpen(false);
        void navigate({ to: '/prefixes', search: (p) => p });
      },
      onError: (err) => {
        toast.error(err instanceof Error ? err.message : 'Error al eliminar');
      },
    });
  }

  if (isLoading) {
    return (
      <div className="p-4">
        <LoadingRows rows={6} />
      </div>
    );
  }

  if (error || !prefix) {
    return (
      <div className="p-4">
        <ErrorState error={error ?? new Error('Prefijo no encontrado')} onRetry={() => void refetch()} />
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col overflow-auto">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 border-b border-border px-4 py-3">
        <div className="flex flex-wrap items-center gap-2">
          <Mono className="text-base font-semibold">{prefix.cidr}</Mono>
          <FamilyBadge family={prefix.family} />
          <RoleBadge role={prefix.role} />
          <StatusBadge status={prefix.status} />
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          {/* Assignments are leaves — the backend rejects carving inside them. */}
          {prefix.role !== 'assignment' && (
            <Button
              size="sm"
              className="h-7 text-xs"
              onClick={() => setNextFreeOpen(true)}
            >
              Asignar siguiente libre
            </Button>
          )}
          {prefix.family === 4 && prefix.role !== 'assignment' && (
            <Button
              variant="outline"
              size="sm"
              className="h-7 text-xs"
              onClick={() => setExplorerOpen(true)}
            >
              Desplegar
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            className="h-7 text-xs"
            onClick={() => setEditOpen(true)}
          >
            Editar
          </Button>
          <Button
            variant="destructive"
            size="sm"
            className="h-7 text-xs"
            onClick={() => setDeleteOpen(true)}
          >
            Eliminar
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="overview" className="flex min-h-0 flex-1 flex-col px-4 py-3">
        <TabsList className="mb-3 h-7 self-start">
          <TabsTrigger value="overview" className="text-xs">Resumen</TabsTrigger>
          <TabsTrigger value="children" className="text-xs">Hijos</TabsTrigger>
          <TabsTrigger value="addresses" className="text-xs">Direcciones</TabsTrigger>
          <TabsTrigger value="utilization" className="text-xs">Utilización</TabsTrigger>
          <TabsTrigger value="audit" className="text-xs">Auditoría</TabsTrigger>
        </TabsList>

        {/* Resumen */}
        <TabsContent value="overview" className="mt-0 min-h-0 flex-1 space-y-4 overflow-auto">
          <dl className="grid grid-cols-[auto,1fr] gap-x-4 gap-y-1.5 text-xs">
            <dt className="text-muted-foreground">CIDR</dt>
            <dd><Mono>{prefix.cidr}</Mono></dd>

            <dt className="text-muted-foreground">Familia</dt>
            <dd><FamilyBadge family={prefix.family} /></dd>

            <dt className="text-muted-foreground">Rol</dt>
            <dd><RoleBadge role={prefix.role} /></dd>

            <dt className="text-muted-foreground">Estado</dt>
            <dd><StatusBadge status={prefix.status} /></dd>

            <dt className="text-muted-foreground">Descripción</dt>
            <dd className="text-foreground">{prefix.description || '—'}</dd>

            <dt className="text-muted-foreground">Creado</dt>
            <dd title={formatTimestamp(prefix.created_at)} className="text-foreground">
              {formatRelative(prefix.created_at)}
            </dd>

            <dt className="text-muted-foreground">Actualizado</dt>
            <dd title={formatTimestamp(prefix.updated_at)} className="text-foreground">
              {formatRelative(prefix.updated_at)}
            </dd>

            {prefix.tags && prefix.tags.length > 0 && (
              <>
                <dt className="text-muted-foreground">Etiquetas</dt>
                <dd className="flex flex-wrap gap-1">
                  {prefix.tags.map((t) => (
                    <Badge key={t} variant="secondary" className="text-[10px]">
                      {t}
                    </Badge>
                  ))}
                </dd>
              </>
            )}
          </dl>

          {/* Metadata */}
          <div className="space-y-1.5">
            <p className="text-xs font-medium text-muted-foreground">Metadata</p>
            <div className="overflow-hidden rounded-md border border-border">
              <Editor
                height={160}
                defaultLanguage="json"
                value={prettyJson(prefix.metadata)}
                theme="vs-dark"
                options={{
                  readOnly: true,
                  minimap: { enabled: false },
                  lineNumbers: 'off',
                  folding: false,
                  scrollBeyondLastLine: false,
                  fontSize: 12,
                }}
              />
            </div>
          </div>
        </TabsContent>

        {/* Hijos */}
        <TabsContent value="children" className="mt-0 min-h-0 flex-1 overflow-hidden">
          <ChildrenTab prefixId={id} />
        </TabsContent>

        {/* Direcciones */}
        <TabsContent value="addresses" className="mt-0 min-h-0 flex-1 overflow-hidden">
          <AddressesTab prefixId={id} />
        </TabsContent>

        {/* Utilización */}
        <TabsContent value="utilization" className="mt-0 min-h-0 flex-1 overflow-auto">
          <UtilizationTab prefixId={id} />
        </TabsContent>

        {/* Auditoría */}
        <TabsContent value="audit" className="mt-0 min-h-0 flex-1 overflow-auto">
          <AuditTab prefixId={id} />
        </TabsContent>
      </Tabs>

      {prefix.role !== 'assignment' && (
        <NextFreeDialog open={nextFreeOpen} onOpenChange={setNextFreeOpen} parent={prefix} />
      )}
      <EditPrefixDialog open={editOpen} onOpenChange={setEditOpen} prefix={prefix} />
      {explorerOpen && (
        <RangeExplorerDialog
          open={explorerOpen}
          onOpenChange={setExplorerOpen}
          initialCidr={prefix.cidr}
        />
      )}

      {/* Delete confirm */}
      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Eliminar prefijo</DialogTitle>
            <DialogDescription>Esta acción no se puede deshacer.</DialogDescription>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            ¿Eliminar <Mono>{prefix.cidr}</Mono>? Esta acción no se puede deshacer.
          </p>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline" size="sm">Cancelar</Button>
            </DialogClose>
            <Button
              variant="destructive"
              size="sm"
              onClick={confirmDelete}
              disabled={deletePrefix.isPending}
            >
              {deletePrefix.isPending ? 'Eliminando…' : 'Eliminar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---- Sub-tabs ----

function ChildrenTab({ prefixId }: { prefixId: string }) {
  const navigate = useNavigate();
  const { data: children = [], isLoading, error } = usePrefixChildren(prefixId);

  return (
    <DataTable
      columns={childrenColumns}
      data={children}
      isLoading={isLoading}
      error={error}
      getRowId={(r) => r.id}
      onRowClick={(r) =>
        void navigate({ to: '/prefixes/$id', params: { id: r.id }, search: (p) => p })
      }
      emptyTitle="Sin hijos"
      emptyDescription="Este prefijo no tiene subredes registradas."
      virtualize={children.length > 50}
      rowHeight={32}
      csvFilename="prefijos-hijos"
    />
  );
}

function AddressesTab({ prefixId }: { prefixId: string }) {
  const { data: addresses = [], isLoading, error } = useAddresses(prefixId);

  return (
    <div className="flex flex-col gap-2">
      <DataTable
        columns={addressColumns}
        data={addresses}
        isLoading={isLoading}
        error={error}
        getRowId={(r) => r.id}
        emptyTitle="Sin direcciones"
        emptyDescription="No hay direcciones IP registradas dentro de este prefijo."
        virtualize={addresses.length > 50}
        rowHeight={32}
        csvFilename="direcciones"
      />
    </div>
  );
}

function UtilizationTab({ prefixId }: { prefixId: string }) {
  const { data: util, isLoading, error } = usePrefixUtilization(prefixId);

  if (isLoading) return <LoadingRows rows={3} />;
  if (error) return <ErrorState error={error} />;
  if (!util) return null;

  return (
    <div className="space-y-4">
      <p className="text-[10px] text-muted-foreground">
        Datos de un solo punto — no hay historial de utilización disponible.
      </p>

      {/* Stats row */}
      <div className="grid grid-cols-3 gap-3">
        {(
          [
            { label: 'Total', value: util.total_addresses },
            { label: 'Usadas', value: util.used_addresses },
            { label: 'Libres', value: util.free_addresses },
          ] as const
        ).map(({ label, value }) => (
          <div
            key={label}
            className="rounded-md border border-border bg-muted/30 px-3 py-2 text-center"
          >
            <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</p>
            <p className="font-mono text-sm font-semibold tabular-nums text-foreground">
              {formatNumber(value)}
            </p>
          </div>
        ))}
      </div>

      {/* Utilization bar */}
      <div className="space-y-1">
        <p className="text-[10px] text-muted-foreground">Ocupación</p>
        <UtilizationBar percent={util.percent_used} showLabel height={8} />
      </div>
    </div>
  );
}

function AuditTab({ prefixId }: { prefixId: string }) {
  const { data: events = [], error, isLoading } = useAudit(1000);
  // The audit API has no server-side filters (all filtering is client-side by
  // design) — narrow the tenant timeline to the events touching this prefix.
  const prefixEvents = events.filter(
    (ev) => ev.resource_type === 'prefix' && ev.resource_id === prefixId,
  );

  if (isLoading) return <LoadingRows rows={4} />;
  if (error) return <ErrorState error={error} />;
  return <AuditTimeline events={prefixEvents} />;
}
